import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { CloudDoneOutlined, CloudOutlined } from '@mui/icons-material';

import { Button } from 'components/Button';
import MainToolbarContent from 'containers/Tiptap/components/tiptap-ui/MainToolbarContent/MainToolbarContent';

import { ActionButton } from 'containers/Tiptap/components/tiptap-templates/ActionButton';
import { SidebarEnum } from 'containers/Tiptap/components/tiptap-templates/types';
import { ErrorContainer } from 'containers/Tiptap/components/tiptap-templates/styles';

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 0;
  margin-bottom: 8px;
  gap: 12px;
`;

const SavedIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 12px;
`;

const SAVED_TOOLTIP_TEXT =
  'Your changes save automatically as a draft. Click "Publish" to save them as a version you can restore to later.';

interface EditorToolbarProps {
  activeSidebar: SidebarEnum | false;
  setActiveSidebar: (sidebar: SidebarEnum | false) => void;
  setShareModalOpen: (open: boolean) => void;
  errorMessage?: string | null;
  hasUnpublishedChanges?: boolean;
  isPublishing?: boolean;
  onPublish?: () => void;
  canShare?: boolean;
}

const getPublishButtonText = (isSaving: boolean, hasUnsavedChanges: boolean): string => {
  if (isSaving) {
    return 'Publishing...';
  }
  if (hasUnsavedChanges) {
    return 'Publish';
  }
  return 'Published';
};

export const EditorToolbar: React.FC<EditorToolbarProps> = ({
  activeSidebar,
  setActiveSidebar,
  setShareModalOpen,
  errorMessage,
  hasUnpublishedChanges = false,
  isPublishing = false,
  onPublish,
  canShare = true,
}) => (
  <>
    <TopBar>
      {onPublish && (
        <>
          <Tooltip title={SAVED_TOOLTIP_TEXT} arrow placement="bottom">
            <SavedIndicator>
              {hasUnpublishedChanges ? (
                <CloudOutlined sx={{ fontSize: 16 }} />
              ) : (
                <CloudDoneOutlined sx={{ fontSize: 16 }} />
              )}
              {hasUnpublishedChanges ? 'Auto-saved · Draft' : 'Auto-saved'}
            </SavedIndicator>
          </Tooltip>
          <Button
            id="save-version-button"
            onClick={onPublish}
            variant={hasUnpublishedChanges ? 'contained' : 'outlined'}
            size="medium"
            disabled={isPublishing || !hasUnpublishedChanges}
          >
            {getPublishButtonText(isPublishing, hasUnpublishedChanges)}
          </Button>
        </>
      )}
      {canShare && (
        <Button variant="outlined" size="medium" onClick={() => setShareModalOpen(true)}>
          Share
        </Button>
      )}
      <ActionButton activeSidebar={activeSidebar} setActiveSidebar={setActiveSidebar} />
    </TopBar>

    {errorMessage && (
      <ErrorContainer>
        <span>Error: {errorMessage}</span>
      </ErrorContainer>
    )}

    <MainToolbarContent />
  </>
);
